import axios from 'axios'
import { APP_BASE } from '@/config/basePath'
import { recordFailedCall } from '@twasta/page-runtime/support/errorRing'
import { appEnvHeaders } from './appEnv'

const api = axios.create({
  // Base-path aware: '/api' in dev, '/ibase/twasta/api' under WildFly.
  baseURL: `${APP_BASE}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

/**
 * Act as a PROJECT USER when a Run session is in force.
 *
 * A project user has no platform cookie to send, so their requests carry the
 * application they are in and the registry session token they signed in with
 * (see `api/runSession`). Attached here, once, rather than in each API module:
 * the Run view reaches transactions, visuals, reports, documents, search and
 * the assistant through a dozen of them, and a module that forgot would fail
 * in a way that looks like a rights problem.
 *
 * Two deliberate exclusions. Platform auth endpoints are left alone — they are
 * about the Twasta account and a project user has none. And an `Authorization`
 * header the caller set itself always wins, because the `/website/...` site
 * endpoints already send their own and must keep addressing their own session.
 */
// Imported lazily and cached: `runSession` reads the app's site-token store,
// which lives in an api module that imports this one. The cycle is real, so
// the import is deferred to first use rather than pulled in at module load.
let runSessionModule: typeof import('@/api/runSession') | null = null

// When each request started, so a failed one can be reported with how long it
// took (a timeout reads very differently from an instant refusal).
const STARTED = '__twastaStartedAt'

api.interceptors.request.use(async (config) => {
  try { (config as unknown as Record<string, unknown>)[STARTED] = performance.now() } catch { /* no clock */ }
  if ((config.url || '').includes('/auth/')) return config
  if (!runSessionModule) runSessionModule = await import('@/api/runSession')
  let extra = runSessionModule.runSessionHeaders()
  // Not a project user: name the application (and its release-management
  // environment) this page is about, when one is bound — see `api/appEnv`.
  // Never both: a project user's session already names its application, and
  // two answers to "which application" on one request is one too many.
  if (!Object.keys(extra).length) extra = appEnvHeaders()
  if (!Object.keys(extra).length) return config
  const headers = config.headers as unknown as {
    Authorization?: unknown
    authorization?: unknown
    set?: (k: string, v: string) => unknown
    [k: string]: unknown
  }
  const hasOwnAuth = !!(headers.Authorization || headers.authorization)
  for (const [key, value] of Object.entries(extra)) {
    if (key === 'Authorization' && hasOwnAuth) continue
    if (typeof headers.set === 'function') headers.set(key, value)
    else headers[key] = value
  }
  return config
})

// URLs that should NOT trigger the refresh interceptor
// (they are expected to return 401 when not logged in, or must not loop:
// /auth/sso is reached via the getMe→sso bootstrap and re-refreshing it would
// re-enter that flow).
// The second-factor steps answer 401 for a WRONG CODE (no session exists yet)
// — refreshing would only replace "that code is not right" with "No refresh
// token". The same holds for a project user's own sign-in on a site.
const SKIP_REFRESH_URLS = [
  '/auth/me', '/auth/login', '/auth/register', '/auth/refresh', '/auth/sso',
  '/auth/mfa/', '/auth/confirm-login-choice', '/site-auth/login', '/site-auth/mfa/',
  // The public marketplace is browsed signed out; a 401 there is not an
  // expired platform session.
  '/marketplace/public/',
]

// 401 codes the site-txn / site-doc endpoints answer about the APPLICATION's
// own session or a page-bound embed token (`routers/website.py`). Not platform
// token expiry, so they skip the refresh below.
const SITE_SESSION_CODES = new Set([
  'SIGN_IN_REQUIRED', 'SESSION_EXPIRED', 'EMBED_TOKEN_EXPIRED', 'EMBED_TOKEN_REQUIRED',
])

// AuthContext registers a callback here so a failed token refresh (long-session
// drop) re-bootstraps auth (which can re-SSO) instead of silently logging out.
// Kept as a module hook so client.ts stays decoupled from React.
let onRefreshFailure: (() => void) | null = null
export function setOnRefreshFailure(cb: (() => void) | null) {
  onRefreshFailure = cb
}

let isRefreshing = false
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = []

const processQueue = (error: unknown) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error)
    else p.resolve(undefined)
  })
  failedQueue = []
}

/**
 * A `responseType: 'blob'` request (file downloads) gets its ERROR body as a
 * Blob too, so `error.response.data.code` is undefined and a perfectly good
 * structured refusal — "this plan doesn't include downloads", "top up your
 * credits" — would surface as an unreadable failure. Re-parse it to JSON so a
 * download refusal reaches the same modal every other refusal does.
 */
async function unwrapBlobError(error: { response?: { data?: unknown } }): Promise<void> {
  const data = error?.response?.data
  if (!(data instanceof Blob)) return
  try {
    const text = await data.text()
    error.response!.data = JSON.parse(text)
  } catch {
    // A genuinely binary or malformed error body stays as-is.
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const requestUrl = originalRequest?.url || ''

    await unwrapBlobError(error)

    // Feed the Support module's error ring: a reporter's "it didn't save" is
    // usually THIS — a refused request seconds earlier — and the report dialog
    // attaches the last few automatically. A 401 is the session lapsing, not a
    // fault worth reporting, and is left out.
    if (error.response?.status !== 401) {
      try {
        const data = error.response?.data
        const detail = data?.detail ?? data?.message ?? error.message
        const started = (originalRequest as Record<string, unknown> | undefined)?.[STARTED]
        const duration = typeof started === 'number' ? Math.round(performance.now() - started) : undefined
        recordFailedCall(originalRequest?.method, `${originalRequest?.baseURL || ''}${requestUrl}`,
          error.response?.status ?? null,
          typeof detail === 'string' ? detail : (detail?.message ?? detail), duration)
      } catch {
        // Diagnostics must never change how a failure propagates.
      }
    }

    // AI access errors (no usable key / platform allowance exhausted) come back
    // as HTTP 402 with a machine `code`. Surface them through the global
    // AiAccessModal (graceful upgrade/add-key flow) instead of a raw toast. We
    // HOLD the caller's promise: if the user adds a key, the modal calls
    // retry() which replays the original request and resolves the original
    // await — a seamless continuation. cancel() rejects with the 402.
    // HTTPException(402, {code: ...}) nests the payload under `detail`, while a
    // handler-raised envelope is already top level. Read whichever carries the
    // code so both reach the modal identically.
    const body = error.response?.data
    const envelope = body?.code ? body : (body?.detail?.code ? body.detail : null)

    if ((error.response?.status === 402 || error.response?.status === 403) && envelope?.code) {
      const d = envelope
      // A project-wallet refusal is the ONE 402 the global modal must not
      // claim: it is the project manager's wallet, not the caller's, and the
      // remedy (top up this project, retry the invite) only makes sense inside
      // the team tab. Let it reject so the caller handles it locally.
      if (d.code === 'INSUFFICIENT_PROJECT_CREDITS') {
        return Promise.reject(error)
      }
      // Marketplace refusals (an organization's wallet, an install's state)
      // are answered by the marketplace screens themselves.
      if (typeof d.code === 'string' && d.code.startsWith('MARKETPLACE_')) {
        return Promise.reject(error)
      }
      const { reportAiAccessError, pickAiAccessFields } =
        await import('@/store/aiAccessStore')
      return new Promise((resolve, reject) => {
        reportAiAccessError({
          ...pickAiAccessFields(d),
          retry: () => resolve(api(originalRequest)),
          cancel: () => reject(error),
        })
      })
    }

    // Don't intercept auth-check or login endpoints
    const shouldSkip = SKIP_REFRESH_URLS.some((url) => requestUrl.includes(url))

    // A project user's 401 is THEIR session expiring, not a platform token
    // going stale — there is no platform token. Refreshing one would fail,
    // and the failure hook would send them to the Twasta login page they have
    // no account for. End the run session instead; the Run view then shows the
    // application's own sign-in.
    //
    // Only a 401 that SAYS so (header X-Twasta-Session, set by
    // project_user_access) ends the session: a platform-only endpoint refusing
    // a project user answers 401 too, and must not sign them out.
    if (error.response?.status === 401) {
      const { isProjectUserRun, endRunSession } = await import('@/api/runSession')
      if (isProjectUserRun()) {
        const headers = (error.response?.headers || {}) as Record<string, string>
        if (headers['x-twasta-session']) endRunSession()
        return Promise.reject(error)
      }
    }

    // A 401 that names a SITE-session answer is about the application's own
    // session or embed token, never the platform cookie — refreshing the
    // cookie cannot change it, and a failed refresh would replace this error
    // (and its code, which the embed's sign-in card and token retry read)
    // with the refresh's own.
    const siteCode = error.response?.data?.error?.code
    if (error.response?.status === 401 && typeof siteCode === 'string'
        && SITE_SESSION_CODES.has(siteCode)) {
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry && !shouldSkip) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then(() => api(originalRequest))
      }
      originalRequest._retry = true
      isRefreshing = true
      try {
        await axios.post(`${APP_BASE}/api/auth/refresh`, {}, { withCredentials: true })
        processQueue(null)
        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError)
        // Don't redirect here — let AuthContext handle the unauthenticated
        // state. Notify it so a long-session drop re-bootstraps (and can
        // re-SSO) rather than falling straight to the login page.
        try { onRefreshFailure?.() } catch { /* never let the hook break the chain */ }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }
    return Promise.reject(error)
  }
)

export default api
